import type { fabric } from 'fabric';

function isLinearLike(obj: fabric.Object): boolean {
  const t = (obj.type || '').toString();
  return t === 'line' || (t === 'path' && !(obj as fabric.Path).fill);
}

/**
 * Paint-bucket fill: object fill/stroke, or artboard background when no target.
 * Returns a short status note for the options bar / UI.
 */
export function applyPaintBucket(
  canvas: fabric.Canvas,
  target: fabric.Object | null | undefined,
  color: string,
  opacity = 1
): { ok: boolean; note: string } {
  if (!target) {
    canvas.setBackgroundColor(color, () => {
      canvas.requestRenderAll();
    });
    return { ok: true, note: 'Artboard background filled' };
  }

  const type = (target.type || '').toString();
  if (type === 'image') {
    return { ok: false, note: 'Images can’t be filled — use Adjustments or Remove BG' };
  }
  if (type === 'activeSelection' || type === 'group') {
    const members =
      type === 'activeSelection'
        ? (target as fabric.ActiveSelection).getObjects()
        : (target as fabric.Group).getObjects();
    let filled = 0;
    members.forEach((obj) => {
      const r = applyPaintBucket(canvas, obj, color, opacity);
      if (r.ok) filled += 1;
    });
    canvas.requestRenderAll();
    return filled
      ? { ok: true, note: `Filled ${filled} object(s)` }
      : { ok: false, note: 'Nothing fillable in selection' };
  }

  if (type === 'line' || isLinearLike(target)) {
    target.set({ stroke: color, opacity });
  } else if (type === 'path' && (target.fill == null || target.fill === '')) {
    target.set({ stroke: color, opacity });
  } else {
    target.set({ fill: color, opacity });
  }
  target.setCoords();
  canvas.requestRenderAll();
  return { ok: true, note: 'Fill applied' };
}
